import Button from '@mui/material/Button/Button';
import Dialog from '@mui/material/Dialog/Dialog';
import DialogActions from '@mui/material/DialogActions/DialogActions';
import DialogContent from '@mui/material/DialogContent/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle/DialogTitle';
import IconButton from '@mui/material/IconButton/IconButton';
import TextField from '@mui/material/TextField/TextField';
import Typography from '@mui/material/Typography/Typography';
import Replay from '@mui/icons-material/Replay';
import { ChangeEvent, useContext, useRef } from 'react';
import { useAppAlert } from '../../Contexts/AppAlertContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { appContext } from '../../Contexts/AppContext';
import { useIsInternalUser } from '../../utils/helpers';
import { INTERNAL_CO } from '../../utils/constants';
import { getCompany } from '../../utils/authenticated-requests/company';

interface SwitchCompanyDialogProps {
  isOpen: boolean;
  handleCloseModal: () => void;
}

export const SwitchCompanyDialog: React.FC<SwitchCompanyDialogProps> = ({
  isOpen,
  handleCloseModal,
}): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company, setCompany, setActiveProject, setAppLoading } =
    useContext(appContext);
  const { addAppAlert } = useAppAlert();
  const isInternalUser: boolean = useIsInternalUser();
  const companyToSwitchRef = useRef<string>('');

  const handleSwitchCompany = async (companyId?: string): Promise<void> => {
    companyToSwitchRef.current = '';
    handleCloseModal();
    if (!companyId) {
      return;
    }
    if (await canSwitch(companyId)) {
      setActiveProject({});
      addAppAlert({
        message: t('ALERT_SUCCESS_SWITCH_COMPANY', { company: companyId }),
        severity: 'success',
      });
      setCompany(companyId);
      navigate(`/${companyId}`);
    } else {
      addAppAlert({
        message: t('ALERT_ERROR_SWITCH_COMPANY', { company: companyId }),
        severity: 'error',
      });
    }
    setAppLoading(false);
  };

  const canSwitch = async (companyId: string): Promise<boolean> => {
    if (!isInternalUser) {
      return false;
    }
    setAppLoading(true);
    try {
      const response = await getCompany(companyId);
      return !!response.company;
    } catch (error) {
      return false;
    }
  };

  const handleChangeCompanyToSwitch = (
    e: ChangeEvent<HTMLInputElement>,
  ): void => {
    companyToSwitchRef.current = e.target.value;
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={isOpen}
      onClose={() => handleSwitchCompany()}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSwitchCompany(companyToSwitchRef.current);
        }}
      >
        <DialogTitle>{t('SWITCH_COMPANY_MODAL_TITLE')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('SWITCH_COMPANY_MODAL_CONTENT')}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={t('LABEL_COMPANYUSER_COMPANYID')}
            placeholder={t('PLACEHOLDER_SWITCH_COMPANY')}
            type="text"
            fullWidth
            onChange={handleChangeCompanyToSwitch}
            sx={{ mt: 2 }}
          />
          <IconButton
            aria-label={t('SWITCH_BACK_COMPANY')}
            title={t('SWITCH_BACK_COMPANY')}
            disabled={company === INTERNAL_CO}
            onClick={() => handleSwitchCompany(INTERNAL_CO)}
            edge="start"
          >
            <Replay />
            <Typography variant="body1">{t('SWITCH_BACK_COMPANY')}</Typography>
          </IconButton>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => handleSwitchCompany()}>
            {t('BUTTON_CANCEL')}
          </Button>
          <Button variant="contained" type="submit">
            {t('BUTTON_CONFIRM')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
